import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import ApiBasedAutoComplete from '../../../globalComponents/apiBasedAutoComplete';
import { SensorConfig } from '../hooks/useCreateOrUpdateDataLogger';
import { DataLoggerDetails } from '../hooks/useDataLoggerDetails';
import DeleteOutlinedIcon from '../../../core-components/assets/icons/DeleteOutlinedIcon';
import { Neutrals } from '../../../core-components/theme/color-palette/colors';
import Autocomplete from '@mui/material/Autocomplete';

interface ConfigurationDialogSecondTabProps {
  formData: Partial<DataLoggerDetails>;
  sensorConfigs: { [key: string]: SensorConfig };
  expandedSensors: string[];
  onSensorConfigChange: (sensorId: string, field: string, value: any) => void;
  onSensorToggle: (sensorId: string) => void;
  onSensorSelect: (sensor: any) => void;
  onSensorRemove: (sensorId: string) => void;
  errors: { [key: string]: string };
  sensorConfigErrors: { [sensorId: string]: { [field: string]: string } };
}

export default function ConfigurationDialogSecondTab({
  formData,
  sensorConfigs,
  expandedSensors,
  onSensorConfigChange,
  onSensorToggle,
  onSensorSelect,
  onSensorRemove,
  errors,
  sensorConfigErrors,
}: ConfigurationDialogSecondTabProps) {
  const { t } = useTranslation();
  const selectedSensors = Object.values(sensorConfigs)
    .filter(cfg => !cfg.isDeleted)
    .map(cfg => cfg.sensor);

  const visibleSensorEntries = Object.entries(sensorConfigs).filter(
    ([, config]) => !config.isDeleted
  );


  return (
    <Box sx={{ width: '100%', mt: 4 }}>
      <Box sx={{ mb: 3 }}>
       
        <ApiBasedAutoComplete
          label={t('dataLogger.secondTab.selectSensors', 'Select Sensors')}
          onSelect={onSensorSelect}
          alternateKeyToPick="sensorTag"
          apiPath="/sensors/getListOfSensorsForDataLogger"
          searchKey="name"
          keyToPick="name"
          idKey="_id"
          multiple={true}
          defaultData={{ plantId: formData.asset?._id || '' }}
          helperText={errors.sensorSelect}
          error={!!errors.sensorSelect}
          selectedOptions={selectedSensors}
          showChipDeleteIcon={false}
          disableCloseOnSelect={true}
          blurOnSelect={false}
          hideClearIcon={true}
          customRenderOptionsLabel={(option) => {
            if (option.name && option.sensorTag) {
              return `${option.name} (${option.sensorTag})`;
            }
            return option.sensorTag || '';
          }}
        />
      </Box>

      {visibleSensorEntries.length === 0 ? (
        <Typography variant="body1" color="text.secondary" textAlign="center" p={3}>
          {t('dataLogger.secondTab.noSensorsAdded', 'No sensors added yet. Use the search above to add sensors to this data logger.')}
        </Typography>
      ) : (

        <Box >
          <Typography variant="h6" gutterBottom>
            {t('dataLogger.secondTab.configureSensors', 'Configure Sensors')} ({visibleSensorEntries.length})
          </Typography>
          {visibleSensorEntries.map(([sensorId, config]) => {
            const sensorErrors = sensorConfigErrors[sensorId] || {};
            const sensorType = config.sensorType || 'boolean';
            const isTotalizerReference = !!config.referenceSensor;
            
            if (!formData._id && config.isNewEntry) {
              if (!config.sensorType) {
                onSensorConfigChange(sensorId, 'sensorType', 'boolean');
              }
              if (!config.scalingFactor) {
                onSensorConfigChange(sensorId, 'scalingFactor', 1);
              }
              if (!config.wordOrder) {
                onSensorConfigChange(sensorId, 'wordOrder', 'CDAB');
              }
            }

            return (

              <Accordion
                key={sensorId}
                expanded={expandedSensors.includes(sensorId)}
                onChange={() => onSensorToggle(sensorId)}
                sx={{ border: '1px solid ', borderColor: Neutrals[100], boxShadow: 'none' }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} >

                  <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
                    <Typography variant="subtitle1">
                      {config.sensor.name || config.sensor.sensorTag}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSensorRemove(sensorId);
                      }}
                      sx={{ ml: 2 }}
                    >
                      <DeleteOutlinedIcon />
                    </IconButton>
                  </Box>

                </AccordionSummary>
                <AccordionDetails>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label={t('common.sensorTag', 'Sensor Tag')}
                      value={config.sensor.sensorTag || ''}
                      onChange={e => onSensorConfigChange(sensorId, 'sensorTag', e.target.value)}
                      error={!!sensorErrors.sensorTag}
                      helperText={sensorErrors.sensorTag}
                      sx={{ mb: 2 }}
                      disabled={true}
                    />
                    <Select
                      fullWidth
                      value={sensorType}
                      onChange={e => onSensorConfigChange(sensorId, 'sensorType', e.target.value)}
                      error={!!sensorErrors.sensorType}
                      displayEmpty
                      sx={{ mb: 2 }}
                    >
                      <MenuItem value="" disabled>{t('dataLogger.secondTab.selectSensorType', 'Select Sensor Type')}</MenuItem>
                      <MenuItem value="analog">{t('dataLogger.secondTab.analog', 'Analog')}</MenuItem>
                      <MenuItem value="boolean">{t('dataLogger.secondTab.boolean', 'Boolean')}</MenuItem>
                    </Select>
                    {sensorErrors.sensorType && (
                      <Typography color="error" variant="caption">{sensorErrors.sensorType}</Typography>
                    )}
                  </Box>
                  <Grid container spacing={2}>
                    {sensorType === 'analog' && (
                      <>
                        <Grid item xs={12} sm={6} >
                          <Autocomplete
                            options={selectedSensors.filter(s => s._id !== sensorId)}
                            getOptionLabel={option => option.name || option.sensorTag || ''}
                            value={config.referenceSensor || null}
                            onChange={(_, value) => {
                              const updates = {
                                referenceSensor: value,
                                referenceSensorPurpose: '',
                                start: '',
                                end: '',
                                type: '',
                                wordOrder: '',
                                scalingFactor: '',
                                function: '',
                                modbusMisfireTotalizer: false
                              };
                              
                              Object.entries(updates).forEach(([field, fieldValue]) => {
                                onSensorConfigChange(sensorId, field, fieldValue);
                              });
                            }}
                            renderInput={params => (
                              <TextField
                                {...params}
                                label={t('dataLogger.secondTab.referenceSensor', 'Reference Sensor')}
                                error={!!sensorErrors.referenceSensor}
                                helperText={sensorErrors.referenceSensor}
                                sx={{ mb: 2 }}
                              />
                            )}
                            isOptionEqualToValue={(option, value) => option._id === value?._id}
                            disabled={selectedSensors.length <= 1}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Select
                            fullWidth
                            value={config.referenceSensorPurpose || ''}
                            onChange={e => onSensorConfigChange(sensorId, 'referenceSensorPurpose', e.target.value)}
                            displayEmpty
                            disabled={!config.referenceSensor}
                            sx={{ mb: 2 }}
                            error={!!sensorErrors.referenceSensorPurpose}
                          >
                            <MenuItem value="" disabled>{t('dataLogger.secondTab.selectPurpose', 'Select Purpose')}</MenuItem>
                            <MenuItem value="totalizer">{t('dataLogger.secondTab.totalizer', 'totalizer')}</MenuItem>
                          </Select>
                          {sensorErrors.referenceSensorPurpose && (
                            <Typography color="error" variant="caption">{sensorErrors.referenceSensorPurpose}</Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            label={t('dataLogger.secondTab.startRegisterAddress', 'Start Register Address')}
                            type="number"
                            value={config.start ?? ''}
                            onChange={e => {
                              const value = Number(e.target.value);
                              if (value < 0) return;
                              onSensorConfigChange(sensorId, 'start', e.target.value);
                            }}
                            onKeyDown={(evt) => ["e", "E", "+", "-", "."].includes(evt.key) && evt.preventDefault()}
                            error={!isTotalizerReference && !!sensorErrors.start}
                            helperText={!isTotalizerReference ? sensorErrors.start : ''}
                            disabled={isTotalizerReference}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            label={t('dataLogger.secondTab.endRegisterAddress', 'End Register Address')}
                            type="number"
                            value={config.end ?? ''}
                            onChange={e => {
                              const value = Number(e.target.value);
                              if (value < 0) return;
                              onSensorConfigChange(sensorId, 'end', e.target.value);
                            }}
                            onKeyDown={(evt) => ["e", "E", "+", "-", "."].includes(evt.key) && evt.preventDefault()}
                            error={!isTotalizerReference && !!sensorErrors.end}
                            helperText={!isTotalizerReference ? sensorErrors.end : ''}
                            disabled={isTotalizerReference}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Select
                            fullWidth
                            value={config.type || ''}
                            onChange={e => onSensorConfigChange(sensorId, 'type', e.target.value)}
                            displayEmpty
                            sx={{ mb: 2 }}
                            error={!isTotalizerReference && !!sensorErrors.type}
                            disabled={isTotalizerReference}
                          >
                            <MenuItem value="" disabled>{t('dataLogger.secondTab.selectType', 'Select Type')}</MenuItem>
                            <MenuItem value="float">{t('dataLogger.secondTab.float', 'Float')}</MenuItem>
                            <MenuItem value="int">{t('dataLogger.secondTab.int', 'Int')}</MenuItem>
                            <MenuItem value="double">{t('dataLogger.secondTab.double', 'Double')}</MenuItem>
                            <MenuItem value="uint">{t('dataLogger.secondTab.uint', 'Uint')}</MenuItem>
                          </Select>
                          {!isTotalizerReference && sensorErrors.type && (
                            <Typography color="error" variant="caption">{sensorErrors.type}</Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Select
                            fullWidth
                            value={config.wordOrder || ''}
                            onChange={e => onSensorConfigChange(sensorId, 'wordOrder', e.target.value)}
                            error={!isTotalizerReference && !!sensorErrors.wordOrder}
                            sx={{ mb: 2 }}
                            disabled={isTotalizerReference}
                            displayEmpty
                          >
                            <MenuItem value="" disabled >{t('dataLogger.secondTab.selectWordOrder', 'Select Word Order')}</MenuItem>
                            <MenuItem value="ABCD">{t('dataLogger.secondTab.wordOrderABCD', 'ABCD - Big Endian')}</MenuItem>
                            <MenuItem value="CDAB">{t('dataLogger.secondTab.wordOrderCDAB', 'CDAB - Little Endian')}</MenuItem>
                            <MenuItem value="BADC">{t('dataLogger.secondTab.wordOrderBADC', 'BADC - Mixed Endian')}</MenuItem>
                            <MenuItem value="DCBA">{t('dataLogger.secondTab.wordOrderDCBA', 'DCBA - Reverse Endian')}</MenuItem>
                          </Select>
                          {!isTotalizerReference && sensorErrors.wordOrder && (
                            <Typography color="error" variant="caption">{sensorErrors.wordOrder}</Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            label={t('dataLogger.secondTab.scalingFactor', 'Scaling Factor')}
                            type="number"
                            value={config.scalingFactor}
                            onChange={e => {
                              const value = Number(e.target.value);
                              if (value < 0) return;
                              onSensorConfigChange(sensorId, 'scalingFactor', e.target.value);
                            }}
                            onKeyDown={(evt) => ["e", "E", "+", "-", "."].includes(evt.key) && evt.preventDefault()}
                            error={!isTotalizerReference && !!sensorErrors.scalingFactor}
                            helperText={!isTotalizerReference ? sensorErrors.scalingFactor : ''}
                            disabled={isTotalizerReference}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            label={t('dataLogger.secondTab.function', 'Function')}
                            value={config.function || ''}
                            onChange={e => onSensorConfigChange(sensorId, 'function', e.target.value)}
                            error={!isTotalizerReference && !!sensorErrors.function}
                            helperText={!isTotalizerReference ? sensorErrors.function : ''}
                            disabled={isTotalizerReference}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!config.modbusMisfireTotalizer}
                                onChange={e => onSensorConfigChange(sensorId, 'modbusMisfireTotalizer', e.target.checked)}
                                color="primary"
                                disabled={isTotalizerReference}
                              />
                            }
                            label={t('dataLogger.secondTab.modbusMisfireTotalizer', 'Modbus Misfire Totalizer')}
                          />
                          {!isTotalizerReference && sensorErrors.modbusMisfireTotalizer && (
                            <Typography color="error" variant="caption">{sensorErrors.modbusMisfireTotalizer}</Typography>
                          )}
                        </Grid>
                      </>
                    )}
                    {sensorType === 'boolean' && (
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          label={t('dataLogger.secondTab.bitIndex', 'Bit Index')}
                          type="number"
                          value={config.bitIndex ?? ''}
                          onChange={e => {
                            const value = Number(e.target.value);
                            if (value < 0) return;
                            onSensorConfigChange(sensorId, 'bitIndex', e.target.value);
                          }}
                          onKeyDown={(evt) => ["e", "E", "+", "-", "."].includes(evt.key) && evt.preventDefault()}
                          error={!!sensorErrors.bitIndex}
                          helperText={sensorErrors.bitIndex}
                        />
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>

            );
          })}
        </Box>
      )}
    </Box>
  );
} 